import { useEffect, useCallback, useState, useMemo } from 'react';
import mapboxgl from 'mapbox-gl';
import {
  FeatureCollection,
  Geometry,
  GeoJsonProperties,
  Polygon,
  MultiPolygon,
} from 'geojson';
import { OutageDetailsByCounty } from '../../../__generated__/pge-types';
import { getPolygonCenter } from '../components/account-marker-overlay/utils';

const ARCGIS_URL =
  'https://services.arcgis.com/P3ePLMYs2RVChkJx/arcgis/rest/services/USA_Boundaries_2023/FeatureServer/3/query';

interface OutageInfo {
  zipCode: string;
  outageCount: number;
}

interface FetchParams {
  zipCodes: string[];
  state?: string;
}

const fetchZipPolygons = async ({
  zipCodes,
  state = 'OR',
}: FetchParams): Promise<FeatureCollection<Geometry, GeoJsonProperties>> => {
  if (!zipCodes.length) {
    return { type: 'FeatureCollection', features: [] };
  }

  const where = `STATE='${state}' AND ZIP_CODE IN (${zipCodes
    .map(z => `'${z}'`)
    .join(',')})`;

  const params = new URLSearchParams({
    where,
    outFields: 'ZIP_CODE,PO_NAME',
    returnGeometry: 'true',
    outSR: '4326',
    f: 'geojson',
  });

  const response = await fetch(`${ARCGIS_URL}?${params.toString()}`);
  const data = await response.json();
  return data as FeatureCollection<Geometry, GeoJsonProperties>;
};

export const useGetZipBoundaries = (
  mapInstance: mapboxgl.Map | null,
  outagesData: OutageDetailsByCounty[],
  shouldRun: boolean = true,
) => {
  const [zipCenters, setZipCenters] = useState<
    { zipCode: string; count: number; center: [number, number] }[]
  >([]);
  const [geoJson, setGeoJson] = useState<
    FeatureCollection<Geometry, GeoJsonProperties>
  >();

  const outages = useMemo(
    () => outagesData?.flatMap(county => county?.zipCodeInfo) ?? [],
    [outagesData],
  );

  const updateLayer = useCallback(
    async (map: mapboxgl.Map) => {
      if (!outages?.length || !shouldRun) {
        return;
      }

      const zipCodes = outages
        .map(o => o && o.zipCode)
        .filter((zipCode): zipCode is string => typeof zipCode === 'string');
      const geojson = await fetchZipPolygons({ zipCodes });

      if (!geojson) {
        return;
      }
      setGeoJson(geojson);

      // compute centers for markers
      const centers: {
        zipCode: string;
        count: number;
        center: [number, number];
      }[] = geojson.features
        .map(f => {
          if (!f?.geometry) {
            return null;
          }
          // const c = turf.centroid(f);
          const coords = getPolygonCenter(f.geometry as Polygon | MultiPolygon);

          const zip = f.properties?.ZIP_CODE;
          const outage = outages.find(o => o?.zipCode === zip);
          return {
            zipCode: zip,
            count: outage?.totalCustomersEffected ?? 0,
            center: coords,
          };
        })
        .filter(Boolean) as any[];

      setZipCenters(centers);
    },
    [outages, shouldRun],
  );

  useEffect(() => {
    if (!mapInstance) {
      return;
    }

    void updateLayer(mapInstance);
  }, [mapInstance, updateLayer]);

  return { zipCenters, geoJson };
};
